import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, Activity } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { ShareButtonDialog } from "@/components/ShareButtonDialog";
import ResourceBar from "@/components/ResourceBar";
import { PrivateChampionshipBracket } from "../-components/PrivateChampionshipBracket";
import { NavigationPrivateChampionship } from "../-components/NavigationPrivateChampionship";
import { generateTextShadow } from "@/lib/utils";
import { t } from "@/lib/i18n";
import { useQueryClient } from "@tanstack/react-query";
import { useChampionshipBracket, useStats } from "@/lib/hooks/useApi";
import type {
  ChampionshipBracketResponse,
  ChampionshipBracketRound,
  ChampionshipBracketPlayer,
  TournamentDetailsResponse,
  Payout,
} from "@/lib/types/api";
import type { IRoundProps } from "@/components/brackets/types/rounds";
import configs from "@/lib/configs";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { PayoutDialog } from "@/components/PayoutDialog";

type PrivateChampionshipSearch = {
  fromCreate?: boolean;
};

type TournamentStatusChangedEvent = {
  type: "tournament_status_changed";
  tournament_id: string;
  old_status: string;
  new_status: string;
};

type HostedChampionshipRoundChangedEvent = {
  type: "hosted_championship_round_changed";
  tournament_id: string;
  round_number: number;
  old_status: string;
  new_status: string;
};

type TournamentScheduleChangedEvent = {
  type: "tournament_schedule_changed";
  tournament_id: string;
  start_date_epoch: number | null;
  end_date_epoch: number | null;
};

function formatEpochForBracket(epochSeconds: number): string {
  const date = new Date(epochSeconds * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function shortenAddress(address: string): string {
  if (!address) return "";
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function bracketPlayerToTeam(
  p: ChampionshipBracketPlayer,
  winnerUserId: number | null,
) {
  return {
    name: p.userDetails.xHandle || shortenAddress(p.userDetails.address),
    profilePicture: p.userDetails.xProfilePic || "",
    activeFrameUrl: p.userDetails.activeFrameUrl || "",
    squadValue: p.squadValue,
    ownerAddress: p.userDetails.address,
    isWinner: !!winnerUserId && winnerUserId === p.userDetails.userId,
  };
}

function getRoundTitle(
  round: ChampionshipBracketRound,
  allRounds: ChampionshipBracketRound[],
): string {
  if (round.isThirdPlaceMatch) {
    const hasMainFinal = allRounds.some(
      (r) => !r.isThirdPlaceMatch && r.matches.length <= 1,
    );
    return hasMainFinal ? "3rd Place" : "Final";
  }
  if (round.matches.length <= 1) return "Final";
  if (round.matches.length === 2) return "Semi Final";
  if (round.matches.length === 4) return "Quarter Final";
  return `Round of ${round.roundNumber}`;
}

function apiRoundsToIRoundProps(
  rounds: ChampionshipBracketRound[],
): IRoundProps[] {
  return rounds.map((round) => {
    const title = getRoundTitle(round, rounds);

    return {
      title,
      seeds: round.matches.map((match) => {
        const p1 = match.players[0];
        const p2 = match.players[1];
        const winner = match.winnerUserId;
        const tbdTeam = {
          name: "TBD",
          profilePicture: "",
          activeFrameUrl: "",
          squadValue: 0,
          ownerAddress: "",
          isWinner: false,
        };

        return {
          id: match.id,
          date: round.startDate
            ? formatEpochForBracket(round.startDate)
            : undefined,
          teams: [
            p1 ? bracketPlayerToTeam(p1, winner) : tbdTeam,
            p2 ? bracketPlayerToTeam(p2, winner) : tbdTeam,
          ],
        };
      }),
    };
  });
}

function bracketTournamentToDetails(
  data: ChampionshipBracketResponse,
): TournamentDetailsResponse {
  return {
    tournamentId: data.tournament.id,
    nextAvailableRushToJoinId: "",
    tournamentName: data.tournament.name,
    tournamentLogo: "",
    tournamentPoster: "",
    tournamentBanner: "",
    tournamentBackground: "",
    tournamentDescription: "",
    tournamentSeed: data.tournament.tournamentSeed,
    tournamentAccount: data.tournament.tournamentAccount,
    payoutList: data.tournament.payoutList as {
      totalPrize: string;
      payout: Payout[];
    },
    maximumPlayer: data.tournament.maximumPlayers,
    maxMultiEntryCount: 1,
    entryUsdc: data.tournament.betAmount,
    entryFreePass: 0,
    betPnl: 0,
    duration: data.config.liveDurationPerRound,
    startDate: data.tournament.startDate ?? 0,
    isJoined: false,
    players: [],
    endDate: data.tournament.endDate ?? 0,
    status: data.tournament.tournamentStatus,
    winners: (data.tournament.winnerUserIds ?? []).map(String),
    trophies: 0,
    trophiesGain: 0,
    joinedPlayers: data.tournament.joinedPlayers,
    createdDate: "",
    totalUsdcReward: 0,
    nextOpenEpoch: 0,
    tournamentType: "user_hosted_championship",
    currentPot: 0,
    loserTrophies: 0,
    totalWinnings: 0,
    winnerDistributeTxSignature: [],
    distributeRefundsTxSignature: [],
    betAmount: data.tournament.betAmount,
    firstWinnerInfo: {
      address: "",
      username: "",
      xHandle: "",
      seekerDomain: "",
      snsDomain: "",
      profilePicture: "",
      activeFrameUrl: "",
      domainType: null,
      solMultiplier: 0,
      usdcMultiplier: 0,
      isVerified: false,
    },
    ownerProfile: {
      profilePicture: "",
      username: "",
      userId: data.tournament.hostedByUserId,
      userPubkey: "",
      domainType: null,
      isLegends: false,
      isVerified: false,
      activeFrameUrl: "",
    },
    hostedTournamentConfig: {
      topWinner: data.config.topWinner,
      isPassword: data.config.isPassword,
    },
    isChampionship: true,
    isOfficial: data.config.isOfficial,
    gameMode: "championship",
  };
}

function PrivateChampionshipDetails() {
  const { tournamentId } = Route.useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const {
    data: bracketData,
    isLoading,
    refetch: refetchBracket,
  } = useChampionshipBracket(tournamentId, { refetchInterval: false });
  const { user } = usePrivy();
  const { data: statsData } = useStats(user?.wallet?.address ?? "");
  const gameWalletAddress = statsData?.embeddedWallet;

  const [shareUrl, setShareUrl] = useState("");
  const [isSseError, setIsSseError] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setShareUrl(`${window.location.origin}${window.location.pathname}`);
    }
  }, []);

  useEffect(() => {
    if (!tournamentId) return;
    let isActive = true;
    const eventSource = new EventSource(
      `${configs.API_BASE_URL}/sse/tournaments/${tournamentId}?kind=hosted_championship`,
    );

    const handleSnapshot = () => {
      if (!isActive) return;
      setIsSseError(false);
    };

    const handleBracketSnapshot = (event: Event) => {
      if (!isActive) return;
      try {
        const messageEvent = event as MessageEvent<string>;
        const payload = JSON.parse(
          messageEvent.data,
        ) as ChampionshipBracketResponse;
        queryClient.setQueryData(
          ["championshipBracket", tournamentId],
          payload,
        );
        setIsSseError(false);
      } catch (err) {
        console.error("Failed to parse bracket snapshot:", err);
      }
    };

    const handlePlayerJoined = () => {
      if (!isActive) return;
      void refetchBracket();
    };

    const handleScheduleChanged = () => {
      if (!isActive) return;
      void refetchBracket();
    };

    const handleStreamError = () => {
      if (!isActive) return;
      setIsSseError(true);
    };

    const handleMessage = (event: MessageEvent<string>) => {
      if (!isActive) return;
      try {
        const payload = JSON.parse(event.data) as Record<string, unknown> & {
          type?: string;
        };
        if (!payload || typeof payload !== "object") return;
        if (payload.type === "tournament_player_joined")
          handlePlayerJoined();
        else if (payload.type === "tournament_schedule_changed")
          handleScheduleChanged();
      } catch (err) {
        console.error("Failed to parse championship message:", err);
      }
    };

    eventSource.addEventListener("snapshot", handleSnapshot);
    eventSource.addEventListener("bracket_snapshot", handleBracketSnapshot);
    eventSource.addEventListener(
      "tournament_player_joined",
      handlePlayerJoined,
    );
    eventSource.addEventListener(
      "tournament_schedule_changed",
      handleScheduleChanged,
    );
    eventSource.addEventListener("message", handleMessage);
    eventSource.addEventListener("error", handleStreamError);
    eventSource.onerror = handleStreamError;

    return () => {
      isActive = false;
      eventSource.close();
    };
  }, [tournamentId, refetchBracket, queryClient]);

  const rounds = bracketData ? apiRoundsToIRoundProps(bracketData.rounds) : [];
  const tournament = bracketData
    ? bracketTournamentToDetails(bracketData)
    : null;

  const hasJoined = useMemo(() => {
    if (!bracketData || !gameWalletAddress) return false;
    return bracketData.rounds.some((round) =>
      round.matches.some((match) =>
        match.players.some((p) => p.userDetails.address === gameWalletAddress),
      ),
    );
  }, [bracketData, gameWalletAddress]);

  const sseErrorOverlay = (
    <Activity mode={isSseError ? "visible" : "hidden"}>
      <div className="fixed inset-0 z-1002! flex items-center justify-center bg-indigo-950/90 backdrop-blur-xs px-6 text-left">
        <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-6 text-center overflow-x-visible">
          <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
            <img
              src="/assets/connection-error.png"
              alt="Connection Error"
              className="h-40 object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold z-100 uppercase">
              <AnimatedWaiting
                prefix="Reconnecting"
                className="text-white!"
                style={{ textShadow: generateTextShadow(1.5) }}
              />
            </div>
          </div>
        </div>
      </div>
    </Activity>
  );

  return (
    <div className="flex flex-1 flex-col h-full overflow-hidden relative">
      <div
        className="absolute top-0 left-0 right-0 h-[25%] pointer-events-none"
        style={{
          backgroundImage: "url(/assets/list-bg-dome.png)",
          backgroundSize: "cover",
          backgroundPosition: "center 3%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-[75%] pointer-events-none bg-[#546C74]"
        style={{
          backgroundImage: "url(/assets/list-bg-dome-2.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="relative flex flex-col flex-1 min-h-0 overflow-hidden">
        <FloatingLeftButton
          onClick={() => navigate({ to: "/hosted-tournament" })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />

        <ResourceBar
          showBalance
          className="absolute top-4 left-14 right-4 flex z-50 pl-3"
        />

        <div className="absolute top-20 right-4 z-50 flex flex-col items-end gap-4">
          <ShareButtonDialog shareUrl={shareUrl} />
        </div>

        <div className="flex-1 flex flex-col overflow-hidden p-2 pt-2 gap-4">
          <div className="shrink-0 flex items-end justify-center h-1/5 pt-6 pb-12 text-center">
            {tournament?.tournamentLogo ? (
              <img
                src={tournament.tournamentLogo}
                alt={t("championship.altTexts.championship")}
                className="h-full w-auto object-contain"
                style={{ imageRendering: "pixelated" }}
              />
            ) : null}
          </div>
          <div className="shrink-0 flex flex-col items-center justify-center px-4 -mt-16 gap-1">
            {isLoading ? (
              <p
                className="text-xl text-white/50 text-center uppercase"
                style={{ textShadow: generateTextShadow(1) }}
              >
                Loading...
              </p>
            ) : (
              <>
                <p className="text-white text-3xl leading-6 text-center uppercase font-bold text-shadow-black">
                  {tournament?.tournamentName ?? ""}
                </p>
                {bracketData && (
                  <>
                    <p className="text-white/70 text-sm text-center text-shadow-black">
                      {bracketData.tournament.joinedPlayers}
                      /{bracketData.tournament.maximumPlayers} Players
                    </p>
                    {bracketData.tournament.payoutList?.payout && (
                      <PayoutDialog
                        payout={bracketData.tournament.payoutList.payout}
                        entryUsdc={bracketData.tournament.betAmount}
                      >
                        <div className="gray-container px-3 py-1 cursor-pointer">
                          <span className="text-sm font-bold text-white text-shadow-black">
                            Prize Details
                          </span>
                        </div>
                      </PayoutDialog>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex-1 min-h-0 flex flex-col mt-6">
            <PrivateChampionshipBracket rounds={rounds} />
          </div>
        </div>

        {tournament && (
          <NavigationPrivateChampionship
            tournament={tournament}
            hasJoined={hasJoined}
            refetchBracket={refetchBracket}
          />
        )}
      </div>
      {sseErrorOverlay}
    </div>
  );
}

export const Route = createFileRoute(
  "/(game)/private-championship/$tournamentId/",
)({
  component: PrivateChampionshipDetails,
  validateSearch: (
    search: Record<string, unknown>,
  ): PrivateChampionshipSearch => {
    return {
      fromCreate: search.fromCreate === "true" || search.fromCreate === true,
    };
  },
});
